/**
 * scoreExport.ts — SessionResult JSON 내보내기(#6).
 *
 * `buildSessionExport`는 저장 레코드를 다운로드용 페이로드(파일명 + JSON 문자열)로
 * 바꾸는 순수 함수다(같은 입력 → 같은 결과, throw 없음). `downloadSessionResult`는
 * 그 페이로드를 브라우저에서 파일로 떨구는 IO 절반(웹 전용, DOM 없으면 무시).
 * raw PCM·음성은 애초에 SessionRecord에 없으므로 내보내도 민감정보가 새지 않는다.
 */

import type { SessionRecord } from '@muzly/shared';

export type SessionExport = {
  filename: string;
  json: string;
};

/** 파일명에 못 쓰는 문자를 '-'로 — 멜로디 id가 비정상이어도 안전한 이름. */
const safeSlug = (value: string): string =>
  value.replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '') || 'session';

/** 저장 레코드 → 다운로드 페이로드(요약 결과 + 메타). 순수. */
export const buildSessionExport = (record: SessionRecord): SessionExport => ({
  filename: `muzly-score-${safeSlug(record.melodyId)}-${record.savedAtMs}.json`,
  json: JSON.stringify(
    {
      kind: 'muzly.session-result',
      version: 1,
      id: record.id,
      savedAtMs: record.savedAtMs,
      melodyId: record.melodyId,
      title: record.title,
      result: record.result,
    },
    null,
    2,
  ),
});

/** 페이로드를 브라우저에서 파일로 저장. DOM이 없으면(SSR/native) 조용히 무시. */
export const downloadSessionResult = (record: SessionRecord): void => {
  if (
    typeof document === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return;
  }
  const { filename, json } = buildSessionExport(record);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};
